import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, ChevronDown, ChevronUp, ArrowLeft, X, SearchX } from 'lucide-react';
import specialtyService from '../services/specialtyService';
import { specialtyTheme } from '../utils/specialtyTheme';
import GlobalLoader from './GlobalLoader';

const DEMAND_LABEL = { high: 'Alta demanda', medium: 'Media demanda', low: 'Baja demanda' };

const SpecialtySelection = ({ selectedSpecialty, onSelect, onBack }) => {
  const [specialties, setSpecialties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMembershipInfo, setShowMembershipInfo] = useState(false);
  const [expandedMembership, setExpandedMembership] = useState(true);
  const [expandedNonMembership, setExpandedNonMembership] = useState(true);
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(selectedSpecialty);

  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        setLoading(true);
        const formattedSpecialties = await specialtyService.getFormattedOnDemandSpecialties();

        // Filtrar solo las especialidades activas
        const activeSpecialties = formattedSpecialties.filter(spec => spec.active);

        // Ordenar por incidencia (high > medium > low)
        const sortedSpecialties = activeSpecialties.sort((a, b) => {
          const priority = { high: 0, medium: 1, low: 2 };
          return priority[a.incidence] - priority[b.incidence];
        });

        setSpecialties(sortedSpecialties);
        setError(null);
      } catch (err) {
        setError('No se pudieron cargar las especialidades. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialties();
  }, []);

  // Filtrar especialidades según búsqueda
  const filterSpecialties = (specialties) => {
    if (!searchTerm.trim()) return specialties;

    const term = searchTerm.toLowerCase();
    return specialties.filter(specialty =>
      specialty.name.toLowerCase().includes(term) ||
      (specialty.name_EN && specialty.name_EN.toLowerCase().includes(term))
    );
  };

  // Separar especialidades por membresía
  const membershipSpecialties = filterSpecialties(
    specialties.filter(spec => spec.include_in_membership === true)
  );

  const nonMembershipSpecialties = filterSpecialties(
    specialties.filter(spec => spec.include_in_membership !== true)
  );

  // Contadores
  const totalMembership = membershipSpecialties.length;
  const totalNonMembership = nonMembershipSpecialties.length;
  const totalFiltered = membershipSpecialties.length + nonMembershipSpecialties.length;

  // Manejar selección de especialidad
  const handleSpecialtySelect = (specialty) => {
    setSelectedSpecialtyId(specialty.id);
    onSelect(specialty.id);
  };

  const renderSpecialtyCard = (specialty) => {
    const { Icon, iconColor, card } = specialtyTheme(specialty.name);
    const isSelected = selectedSpecialtyId === specialty.id;
    return (
      <button
        key={specialty.id}
        onClick={() => handleSpecialtySelect(specialty)}
        className={`press-scale-card ${card} p-4 md:p-5 rounded-card transition-transform flex flex-col items-center justify-center min-h-[130px] md:min-h-[160px] ${
          isSelected ? 'ring-2 ring-brand-deep ring-offset-2 ring-offset-warm-canvas' : ''
        }`}
      >
        <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white/70 mb-3" style={{ color: iconColor }}>
          <Icon size={24} aria-hidden="true" />
        </span>
        <h3 className="text-sm md:text-base font-semibold text-brand-deep mb-2 text-center leading-tight tracking-[-0.01em]">
          {specialty.name.length > 15 ? `${specialty.name.substring(0, 15)}...` : specialty.name}
        </h3>

        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/70 text-brand-deep/80">
          {DEMAND_LABEL[specialty.incidence] || DEMAND_LABEL.low}
        </span>
      </button>
    );
  };

  // Estado de carga
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 min-h-screen">
        {onBack && (
          <button onClick={onBack} className="press-scale mb-8 text-brand flex items-center gap-2 hover:gap-3 transition-all font-semibold">
            <ArrowLeft size={20} aria-hidden="true" />
            Volver
          </button>
        )}
        <GlobalLoader
          title="Buscando especialidades"
          subtitle="Estamos cargando las opciones disponibles..."
          overlay={false}
        />
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 min-h-screen">
        {onBack && (
          <button onClick={onBack} className="press-scale mb-8 text-brand flex items-center gap-2 hover:gap-3 transition-all font-semibold">
            <ArrowLeft size={20} aria-hidden="true" />
            Volver
          </button>
        )}

        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-brand-deep mb-4 tracking-tight">Error al cargar</h2>
          <p className="text-lg text-red-600 mb-6">{error}</p>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="press-scale px-6 py-3 bg-brand text-white rounded-full font-semibold hover:bg-brand-dark transition-colors"
            >
              Reintentar
            </button>
            {onBack && (
              <button
                onClick={onBack}
                className="press-scale px-6 py-3 border-[1.5px] border-brand text-brand rounded-full font-semibold hover:bg-brand-tint transition-colors"
              >
                Volver al inicio
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 md:py-8 min-h-screen">
      {/* Botón de volver */}
      {onBack && (
        <button
          onClick={onBack}
          className="press-scale mb-6 text-brand flex items-center gap-2 hover:gap-3 transition-all text-sm md:text-base font-semibold"
        >
          <ArrowLeft size={20} aria-hidden="true" />
          Volver
        </button>
      )}

      {/* Header */}
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-[clamp(1.4rem,3vw,2rem)] font-bold text-brand-deep mb-2 md:mb-3 tracking-[-0.025em]">
          {selectedSpecialtyId ? 'Cambiar especialidad' : (<>Elige tu <span className="text-brand">especialidad.</span></>)}
        </h2>
        <p className="text-sm md:text-base text-text-body">
          {selectedSpecialtyId
            ? 'Selecciona otra especialidad o continúa con la actual'
            : 'Selecciona el área de atención que necesitas'}
        </p>
      </div>

      {/* Barra de búsqueda */}
      <div className="mb-6 md:mb-8">
        <div className="relative">
          <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-brand-deep/50" size={20} aria-hidden="true" />
          <input
            type="text"
            placeholder="Buscar especialidad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 md:pl-12 pr-8 md:pr-12 py-3 border border-brand-100 rounded-input focus:border-brand focus:ring-2 focus:ring-brand/30 focus:outline-none transition text-base bg-surface-card"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              aria-label="Limpiar búsqueda"
              className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 text-brand-deep/50 hover:text-brand-deep"
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="flex justify-between items-center mt-2">
          <p className="ds-caption text-text-mute">
            {searchTerm ? (
              <>Mostrando {totalFiltered} de {specialties.length} especialidades</>
            ) : (
              <>{specialties.length} especialidad{specialties.length !== 1 ? 'es' : ''} disponible{specialties.length !== 1 ? 's' : ''}</>
            )}
          </p>
          {searchTerm && totalFiltered === 0 && (
            <p className="ds-caption text-red-500">No se encontraron especialidades</p>
          )}
        </div>
      </div>

      {/* Sección 1: Incluidas en membresía */}
      {totalMembership > 0 && (
        <div className="mb-8 md:mb-10">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-mint-pill p-2 rounded-input">
                <CheckCircle className="text-mint-deep" size={22} aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-base md:text-xl font-bold text-brand-deep">Incluidas en membresía</h3>
                <p className="ds-caption text-text-mute">Sin costo adicional para usuarias activas</p>
              </div>
            </div>
            <button
              onClick={() => setExpandedMembership(!expandedMembership)}
              aria-label={expandedMembership ? 'Contraer' : 'Expandir'}
              className="md:hidden text-brand press-scale"
            >
              {expandedMembership ? <ChevronUp size={20} aria-hidden="true" /> : <ChevronDown size={20} aria-hidden="true" />}
            </button>
          </div>

          {searchTerm && totalMembership === 0 ? (
            <div className="text-center py-6 bg-cool-canvas rounded-card">
              <p className="text-text-body text-sm">No hay especialidades de membresía que coincidan con tu búsqueda</p>
            </div>
          ) : (
            <>
              {/* Versión móvil */}
              <div className="md:hidden">
                {expandedMembership && (
                  <div className="grid grid-cols-2 gap-3">
                    {membershipSpecialties.map(renderSpecialtyCard)}
                  </div>
                )}
              </div>

              {/* Versión desktop */}
              <div className="hidden md:block">
                <div className="grid md:grid-cols-3 gap-4">
                  {membershipSpecialties.map(renderSpecialtyCard)}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sección 2: Otras especialidades */}
      {totalNonMembership > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div>
              <h3 className="text-base md:text-xl font-bold text-brand-deep">Especialidades extras</h3>
              <p className="ds-caption text-text-mute">Consulta con nuestras doctoras certificadas</p>
            </div>
            <button
              onClick={() => setExpandedNonMembership(!expandedNonMembership)}
              aria-label={expandedNonMembership ? 'Contraer' : 'Expandir'}
              className="md:hidden text-brand press-scale"
            >
              {expandedNonMembership ? <ChevronUp size={20} aria-hidden="true" /> : <ChevronDown size={20} aria-hidden="true" />}
            </button>
          </div>

          {searchTerm && totalNonMembership === 0 ? (
            <div className="text-center py-6 bg-cool-canvas rounded-card">
              <p className="text-text-body text-sm">No hay otras especialidades que coincidan con tu búsqueda</p>
            </div>
          ) : (
            <>
              {/* Versión móvil */}
              <div className="md:hidden">
                {expandedNonMembership && (
                  <div className="grid grid-cols-2 gap-3">
                    {nonMembershipSpecialties.map(renderSpecialtyCard)}
                  </div>
                )}
              </div>

              {/* Versión desktop */}
              <div className="hidden md:block">
                <div className="grid md:grid-cols-3 gap-4">
                  {nonMembershipSpecialties.map(renderSpecialtyCard)}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sin resultados */}
      {totalFiltered === 0 && !loading && (
        <div className="text-center py-12 bg-surface-card rounded-card">
          <SearchX className="mx-auto mb-4 text-brand/50" size={44} aria-hidden="true" />
          <h3 className="text-lg md:text-xl font-bold text-brand-deep mb-2">No hay especialidades disponibles</h3>
          <p className="text-text-body mb-6 text-sm">Por el momento no hay especialidades que coincidan con tu búsqueda.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setSearchTerm('')}
              className="press-scale px-4 py-2 border-[1.5px] border-brand text-brand rounded-full font-semibold hover:bg-brand-tint transition-colors"
            >
              Limpiar búsqueda
            </button>
            <button
              onClick={() => window.location.reload()}
              className="press-scale px-4 py-2 bg-brand text-white rounded-full font-semibold hover:bg-brand-dark transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Información sobre membresía (solo móvil) */}
      {totalMembership > 0 && (
        <>
          <div className="md:hidden mt-6">
            <button
              onClick={() => setShowMembershipInfo(!showMembershipInfo)}
              className="press-scale w-full flex items-center justify-between p-4 bg-brand-50 rounded-input"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="text-brand" size={18} aria-hidden="true" />
                <span className="font-semibold text-brand-deep text-sm">¿Qué significa "incluida en membresía"?</span>
              </div>
              <ChevronDown className={`transform transition-transform text-brand ${showMembershipInfo ? 'rotate-180' : ''}`} size={18} aria-hidden="true" />
            </button>

            {showMembershipInfo && (
              <div className="mt-2 p-4 bg-surface-card rounded-input border border-brand-100 animate-slideDown">
                <p className="text-sm text-text-body leading-relaxed">
                  Estas especialidades están disponibles sin costo adicional para pacientes con membresía Daisies activa.
                  Si no tienes membresía, puedes agendar igualmente con un costo adicional por consulta.
                </p>
              </div>
            )}
          </div>

          {/* Información sobre membresía (desktop) */}
          <div className="hidden md:block mt-8 p-6 bg-cool-canvas rounded-card">
            <div className="flex items-start gap-4">
              <div className="bg-surface-card p-3 rounded-full shadow-soft">
                <CheckCircle className="text-brand" size={22} aria-hidden="true" />
              </div>
              <div>
                <h4 className="font-semibold text-brand-deep mb-2">¿Qué significa "incluida en membresía"?</h4>
                <p className="text-text-body text-sm leading-relaxed">
                  Estas especialidades están disponibles sin costo adicional para pacientes con membresía Daisies activa.
                  Si no tienes membresía, puedes agendar igualmente con un costo adicional por consulta.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CTA si hay selección */}
      {selectedSpecialtyId && (
        <div className="mt-8 pt-6 border-t border-brand-100">
          <div className="flex flex-col md:flex-row gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="press-scale md:flex-1 px-6 py-3 border-[1.5px] border-brand text-brand rounded-full font-semibold hover:bg-brand-tint transition-colors"
              >
                Cancelar
              </button>
            )}
            <button
              onClick={() => onSelect(selectedSpecialtyId)}
              className="press-scale md:flex-1 px-6 py-3 bg-brand text-white rounded-full font-semibold hover:bg-brand-dark transition-colors shadow-brand"
            >
              {selectedSpecialtyId === selectedSpecialty
                ? 'Continuar con esta especialidad'
                : 'Seleccionar esta especialidad'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialtySelection;
